import { customSeriesDefaultOptions } from 'lightweight-charts'

/**
 * BoxFillSeries — Relleno de cajas tipo FVG/OB/IFVG/OTE en una sola serie custom.
 *
 * Cada punto de datos puede ser un "whitespace" (upper/lower null) o una vela
 * activa con upper/lower constantes. La serie dibuja un polígono relleno por
 * cada tramo contiguo de puntos activos del mismo color. Permite apilar
 * múltiples cajas en una sola serie, separadas por whitespace, lo que evita
 * crear una serie por cada caja.
 */
class BoxFillPaneRenderer {
  constructor() {
    this._data = null
    this._options = null
  }

  update(data, options) {
    this._data = data
    this._options = options
  }

  draw(target, priceConverter) {
    if (!this._data || !this._options) return
    const { bars } = this._data
    if (bars.length < 2) return

    // No recortar al rango visible: cada caja es una serie propia de 2 puntos
    // (origen y fin). Si el fin cae fuera de la ventana visible (p.ej. una
    // zona activa que llega al presente mientras el usuario hace pan atrás),
    // recortar al rango dejaba la caja invisible con la etiqueta pintada.
    // El canvas ya clipea lo que queda fuera de pantalla.
    const from = 0
    const to = bars.length - 1
    if (from >= to) return

    target.useMediaCoordinateSpace(scope => {
      const ctx = scope.context
      const paneWidth = scope.mediaSize?.width ?? 0
      let i = from
      while (i <= to) {
        const bar = bars[i]
        const d = bar?.originalData
        if (!d || d.upper == null || d.lower == null || Number.isNaN(d.upper) || Number.isNaN(d.lower)) {
          i++
          continue
        }
        const color = d.fillColor || d.color || this._options.fillColor || '#64748b'
        const alpha = d.alpha != null ? d.alpha : (this._options.alpha ?? 0.18)
        const borderColor = d.borderColor || d.color || this._options.fillColor || '#64748b'
        const borderWidth = d.borderWidth != null ? d.borderWidth : (this._options.borderWidth ?? 0)
        const borderStyle = d.borderStyle || this._options.borderStyle || 'solid'
        // Extender el tramo mientras el color sea idéntico y haya datos
        let segEnd = i
        while (segEnd + 1 <= to) {
          const next = bars[segEnd + 1]?.originalData
          if (!next || next.upper == null || next.lower == null) break
          if ((next.fillColor || next.color || this._options.fillColor) !== color) break
          if ((next.alpha != null ? next.alpha : (this._options.alpha ?? 0.18)) !== alpha) break
          segEnd++
        }

        const drawFrom = i > from ? i - 1 : i
        const drawTo = segEnd < to ? segEnd + 1 : segEnd

        // Las barras fuera de la ventana visible llegan con x=NaN (el time
        // scale solo asigna coordenadas al rango visible). Se sustituyen por
        // el borde del panel: una caja que continúa fuera de pantalla se
        // dibuja hasta el borde en vez de desaparecer (antes un NaN en la
        // path anulaba todo el polígono y la caja no se pintaba).
        //
        // extendRight: los puntos con tiempo a futuro (post última vela) el
        // time scale los apila siempre en el índice lastIndex+1 (~1 bar tras
        // la última vela), nunca dentro del whitespace — "la pared". Un punto
        // marcado con extendRight se dibuja en el borde derecho del panel.
        const xOf = (k, fallback) => {
          const b = bars[k]
          if (b?.originalData?.extendRight) return paneWidth
          return Number.isFinite(b?.x) ? b.x : fallback
        }
        const yPairOf = (k) => {
          const b = bars[k]
          if (!b?.originalData) return null
          const { upper, lower } = b.originalData
          if (!Number.isFinite(upper) || !Number.isFinite(lower)) return null
          const yUpper = priceConverter(upper)
          const yLower = priceConverter(lower)
          if (yUpper == null || yLower == null) return null
          return { yUpper, yLower }
        }

        const points = []
        let minY = Infinity
        let maxY = -Infinity
        const firstPair = yPairOf(drawFrom) ?? yPairOf(i)
        const lastPair = yPairOf(drawTo) ?? yPairOf(segEnd)
        if (!firstPair || !lastPair) {
          i = segEnd + 1
          continue
        }
        for (let k = drawFrom; k <= drawTo; k++) {
          const pair = yPairOf(k)
          if (!pair) continue
          points.push({ x: xOf(k, k === drawFrom ? 0 : paneWidth), y: pair.yUpper })
          minY = Math.min(minY, pair.yUpper, pair.yLower)
          maxY = Math.max(maxY, pair.yUpper, pair.yLower)
        }
        for (let k = drawTo; k >= drawFrom; k--) {
          const pair = yPairOf(k)
          if (!pair) continue
          points.push({ x: xOf(k, k === drawTo ? paneWidth : 0), y: pair.yLower })
        }

        if (points.length > 2 && minY !== Infinity && maxY !== Infinity) {
          ctx.beginPath()
          let hasMove = false
          for (const p of points) {
            if (!hasMove) {
              ctx.moveTo(p.x, p.y)
              hasMove = true
            } else {
              ctx.lineTo(p.x, p.y)
            }
          }
          if (hasMove) {
            ctx.closePath()
            ctx.fillStyle = this._withAlpha(color, alpha)
            ctx.fill()
            if (borderWidth > 0) {
              ctx.strokeStyle = this._withAlpha(borderColor, Math.min(1, alpha + 0.3))
              ctx.lineWidth = borderWidth
              ctx.setLineDash(this._dashStyle(d.borderStyle || this._options.borderStyle || 'solid'))
              ctx.stroke()
              ctx.setLineDash([])
            }
          }
        }

        i = segEnd + 1
      }
    })
  }

  _dashStyle(style) {
    switch (style) {
      case 'dashed': return [6, 3]
      case 'dotted': return [2, 2]
      default: return []
    }
  }

  _withAlpha(color, alpha) {
    if (color.startsWith('rgba')) {
      return color.replace(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/, `rgba($1, $2, $3, ${alpha})`)
    }
    if (color.startsWith('rgb(')) {
      const rgb = color.match(/\d+/g)
      return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`
    }
    if (color.startsWith('#')) {
      const hex = color.replace('#', '')
      if (hex.length === 6) {
        const bigint = parseInt(hex, 16)
        const r = (bigint >> 16) & 255
        const g = (bigint >> 8) & 255
        const b = bigint & 255
        return `rgba(${r}, ${g}, ${b}, ${alpha})`
      }
    }
    return color
  }
}

export class BoxFillSeries {
  constructor() {
    this._renderer = new BoxFillPaneRenderer()
  }

  renderer() {
    return this._renderer
  }

  update(data, seriesOptions) {
    this._renderer.update(data, seriesOptions)
  }

  priceValueBuilder(plotRow) {
    return [plotRow.upper, plotRow.lower]
  }

  isWhitespace(data) {
    return data == null || data.upper == null || data.lower == null
  }

  defaultOptions() {
    return {
      ...customSeriesDefaultOptions,
      color: 'rgba(0,0,0,0)',
      fillColor: 'rgba(100,100,139,0.18)',
      alpha: 0.18,
      borderWidth: 0,
      borderStyle: 'solid',
    }
  }
}
